import type { Team, Match, Venue } from '../../lib/types.js';

export type GroupedTeams = ReadonlyArray<{ group: string; teams: Team[] }>;

export const KNOCKOUT_STAGE_ORDER = ['r32', 'r16', 'qf', 'sf', '3rd', 'final'] as const;
export type KnockoutStage = (typeof KNOCKOUT_STAGE_ORDER)[number];

export function getActiveKnockoutStage(matches: readonly Match[]): KnockoutStage {
  // Active = earliest knockout stage that still has at least one unscored match
  const unscoredStages = new Set(
    matches.filter((m) => m.stage !== 'group' && !m.score).map((m) => m.stage),
  );
  for (const stage of KNOCKOUT_STAGE_ORDER) {
    if (unscoredStages.has(stage)) return stage;
  }
  // Fallback: latest stage that has any knockout matches (tournament complete)
  const present = new Set(matches.filter((m) => m.stage !== 'group').map((m) => m.stage));
  for (let i = KNOCKOUT_STAGE_ORDER.length - 1; i >= 0; i--) {
    if (present.has(KNOCKOUT_STAGE_ORDER[i])) return KNOCKOUT_STAGE_ORDER[i];
  }
  return 'r32';
}

export function groupTeams(teams: readonly Team[]): GroupedTeams {
  const map = new Map<string, Team[]>();
  for (const t of teams) {
    const arr = map.get(t.group) ?? [];
    arr.push(t);
    map.set(t.group, arr);
  }
  const sorted = [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([group, list]) => ({
      group,
      teams: [...list].sort((a, b) => a.name_en.localeCompare(b.name_en)),
    }));
  return sorted;
}

export function filterTeamsByQuery(grouped: GroupedTeams, query: string, lang = 'en'): GroupedTeams {
  const q = query.trim().toLowerCase();
  if (q === '') return grouped;
  const nameKey = `name_${lang}`;
  const out: { group: string; teams: Team[] }[] = [];
  for (const { group, teams } of grouped) {
    const matched = teams.filter((t) => {
      const localized = ((t as unknown as Record<string, string>)[nameKey] ?? t.name_en).toLowerCase();
      return localized.includes(q) || t.name_en.toLowerCase().includes(q);
    });
    if (matched.length > 0) out.push({ group, teams: matched });
  }
  return out;
}

export function buildOpponentMap(matches: readonly Match[], stage: string): Map<string, string> {
  const map = new Map<string, string>();
  for (const m of matches) {
    if (m.stage === stage) {
      map.set(m.home, m.away);
      map.set(m.away, m.home);
    }
  }
  return map;
}

export function pairTeamsForKnockout(
  matches: readonly Match[],
  teams: readonly Team[],
  venues: readonly Venue[],
  lang = 'en',
  stage = 'r32',
): GroupedTeams {
  const teamByCode = new Map(teams.map((t) => [t.code, t]));
  const venueById = new Map(venues.map((v) => [v.id, v]));
  const r32 = matches
    .filter((m) => m.stage === stage)
    .sort((a, b) => a.kickoff_utc.localeCompare(b.kickoff_utc));

  return r32.map((m) => {
    const home = teamByCode.get(m.home)!;
    const away = teamByCode.get(m.away)!;
    const venue = venueById.get(m.venue_id);
    const date = new Intl.DateTimeFormat(lang === 'ar' ? 'ar' : lang, {
      month: 'short', day: 'numeric', timeZone: 'UTC',
    }).format(new Date(m.kickoff_utc));
    const venueLabel = venue ? `${venue.name} · ${venue.city}` : '';
    return { group: `${date}||${venueLabel}`, teams: [home, away] };
  });
}

export function filterPairsByQuery(
  paired: GroupedTeams,
  query: string,
  lang = 'en',
  opponentMap: Map<string, string> = new Map(),
): GroupedTeams {
  const q = query.trim().toLowerCase();
  if (q === '') return paired;
  const nameKey = `name_${lang}`;
  const out: { group: string; teams: Team[] }[] = [];
  for (const { group, teams } of paired) {
    const matches = (t: Team) => {
      const localized = ((t as unknown as Record<string, string>)[nameKey] ?? t.name_en).toLowerCase();
      return localized.includes(q) || t.name_en.toLowerCase().includes(q);
    };
    if (teams.some(matches)) {
      out.push({ group, teams });
    }
  }
  return out;
}
